import { collection, query, orderBy, limit, getDocs, doc, updateDoc, Timestamp, runTransaction, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import { NotificationData, NotificationCounts } from '../types/notification';

class InAppNotificationService {

  /**
   * Fetch notifications for the current user
   * @param userId - The user ID to fetch notifications for
   * @returns Promise with notifications and counts
   */
  async getNotifications(userId: string): Promise<{ success: boolean; notifications: NotificationData[]; counts: NotificationCounts }> {
    try {
      // Fetch notifications directly from Firestore
      const notificationsRef = collection(db, 'notifications', userId, 'notifications');
      const notificationsQuery = query(notificationsRef, orderBy('timestamp', 'desc'), limit(40));
      const snapshot = await getDocs(notificationsQuery);
      
      const notifications: NotificationData[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        notifications.push({
          id: doc.id,
          ...data,
          // Ensure timestamp is properly formatted
          timestamp: data.timestamp instanceof Timestamp 
            ? { seconds: data.timestamp.seconds, nanoseconds: data.timestamp.nanoseconds }
            : data.timestamp
        } as NotificationData);
      });

      // Get notification counts
      const counts = await this.getNotificationCounts(userId);
      
      return {
        success: true,
        notifications,
        counts
      };
    } catch (error) {
      console.error('Error fetching notifications:', error);
      throw error;
    }
  }

  /**
   * Get notification counts for a user
   * @param userId - The user ID to get counts for
   * @returns Promise<NotificationCounts>
   */
  private async getNotificationCounts(userId: string): Promise<NotificationCounts> {
    try {
      const notificationsRef = collection(db, 'notifications', userId, 'notifications');
      const snapshot = await getDocs(notificationsRef);
      
      const total = snapshot.size;
      let unread = 0;
      
      snapshot.forEach(doc => {
        const data = doc.data();
        if (!data.read) {
          unread++;
        }
      });
      
      return { total, unread };
    } catch (error) {
      console.warn('Error getting notification counts:', error);
      return { total: 0, unread: 0 };
    }
  }

  /**
   * Mark a specific notification as read
   * @param userId - The user ID
   * @param notificationId - The ID of the notification to mark as read
   * @returns Promise<boolean>
   */
  async markAsRead(userId: string, notificationId: string): Promise<boolean> {
    try {
      await runTransaction(db, async (transaction) => {
        const notificationRef = doc(db, 'notifications', userId, 'notifications', notificationId);
        const userNotificationDocRef = doc(db, 'notifications', userId);

        // Read the notification first to check if it's currently unread
        const notificationDoc = await transaction.get(notificationRef);
        
        if (!notificationDoc.exists()) {
          throw new Error(`Notification ${notificationId} not found`);
        }

        const notificationData = notificationDoc.data();
        
        // Only update if the notification is currently unread
        if (!notificationData?.read) {
          // Mark notification as read
          transaction.update(notificationRef, { read: true });

          // Update user notification document to decrease unread count
          const userNotificationDoc = await transaction.get(userNotificationDocRef);
          if (userNotificationDoc.exists()) {
            const currentData = userNotificationDoc.data();
            const currentUnread = currentData?.unread || 0;
            transaction.update(userNotificationDocRef, {
              unread: Math.max(0, currentUnread - 1),
              lastUpdated: Timestamp.now()
            });
          }
        }
      });
      return true;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      throw error;
    }
  }

  /**
   * Mark all notifications as read for the current user
   * @param userId - The user ID
   * @returns Promise<boolean>
   */
  async markAllAsRead(userId: string): Promise<boolean> {
    try {
      // First get all unread notifications, then update them
      const notificationsRef = collection(db, 'notifications', userId, 'notifications');
      const userNotificationDocRef = doc(db, 'notifications', userId);
      const unreadQuery = query(notificationsRef, where('read', '==', false));
      const unreadSnapshot = await getDocs(unreadQuery);
      
      // Use a transaction to update everything atomically
      await runTransaction(db, async (transaction) => {
        // Check if user notification document exists
        const userDoc = await transaction.get(userNotificationDocRef);
        
        // Mark all unread notifications as read
        unreadSnapshot.docs.forEach(doc => {
          transaction.update(doc.ref, { read: true });
        });

        // Set unread count to 0 in user document
        if (userDoc.exists()) {
          transaction.update(userNotificationDocRef, {
            unread: 0,
            lastUpdated: Timestamp.now()
          });
        } else {
          // Create the user document if it doesn't exist
          transaction.set(userNotificationDocRef, {
            unread: 0,
            lastUpdated: Timestamp.now()
          });
        }
      });
      return true;
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      throw error;
    }
  }

  /**
   * Format notification timestamp for display
   * @param timestamp - Firebase timestamp object
   * @returns string - Formatted time string
   */
  formatNotificationTime(timestamp: { seconds: number; nanoseconds: number } | any): string {
    try {
      let date: Date;
      
      // Handle different timestamp formats
      if (timestamp && typeof timestamp === 'object') {
        if (timestamp.seconds !== undefined) {
          // Firebase Timestamp format
          date = new Date(timestamp.seconds * 1000);
        } else if (timestamp._seconds !== undefined) {
          // Alternative Firebase Timestamp format
          date = new Date(timestamp._seconds * 1000);
        } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
          // Firebase Timestamp object with toDate method
          date = timestamp.toDate();
        } else {
          // Try to parse as regular Date
          date = new Date(timestamp);
        }
      } else {
        // Fallback to current time
        console.warn('Invalid timestamp format:', timestamp);
        return 'Unknown time';
      }

      // Validate the date
      if (isNaN(date.getTime())) {
        console.warn('Invalid date created from timestamp:', timestamp);
        return 'Invalid date';
      }

      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / 60000);

      if (diffInMinutes < 1) {
        return 'Just now';
      } else if (diffInMinutes < 60) {
        return `${diffInMinutes}m ago`;
      } else if (diffInMinutes < 1440) { // Less than 24 hours
        const hours = Math.floor(diffInMinutes / 60);
        return `${hours}h ago`;
      } else {
        const days = Math.floor(diffInMinutes / 1440);
        if (days === 1) {
          return 'Yesterday';
        } else if (days < 7) {
          return `${days}d ago`;
        } else {
          return date.toLocaleDateString();
        }
      }
    } catch (error) {
      console.error('Error formatting notification time:', error, timestamp);
      return 'Unknown time';
    }
  }

  /**
   * Get the appropriate icon for notification type
   * @param type - The notification type
   * @returns string - Icon class or emoji
   */
  getNotificationIcon(type: string): string {
    switch (type) {
      case 'health_data':
        return '🏥';
      case 'activity_data':
        return '🏃‍♂️';
      case 'reminder':
        return '⏰';
      case 'achievement':
        return '🏆';
      case 'social':
        return '👥';
      case 'system':
        return '⚙️';
      default:
        return '📱';
    }
  }

  /**
   * Get notification type display name
   * @param type - The notification type
   * @returns string - Human readable type name
   */
  getNotificationTypeName(type: string): string {
    switch (type) {
      case 'health_data':
        return 'Health Data';
      case 'activity_data':
        return 'Activity Data';
      case 'reminder':
        return 'Reminder';
      case 'achievement':
        return 'Achievement';
      case 'social':
        return 'Social';
      case 'system':
        return 'System';
      case 'data_update':
        return 'Data Update';
      default:
        return 'Notification';
    }
  }

  /**
   * Navigate to notification action endpoint
   * @param actionEndpoint - The endpoint to navigate to
   * @param navigate - React Router navigation function for internal routes
   */
  handleNotificationAction(actionEndpoint: string, navigate?: (path: string) => void): void {
    // Handle different types of navigation
    if (actionEndpoint.startsWith('http')) {
      // External link
      window.open(actionEndpoint, '_blank');
    } else {
      // Internal navigation
      if (navigate) {
        // Use React Router navigation to preserve state
        navigate(actionEndpoint);
      } else {
        // Fallback to window.location.href if navigate function not provided
        console.warn('No navigate function provided, falling back to window.location.href');
        window.location.href = actionEndpoint;
      }
    }
  }
}

export const inAppNotificationService = new InAppNotificationService();